/**
 * AASB 112 — Income Taxes. Rulepack v1.0.0.
 * Reconciles current tax expense from accounting profit, permanent differences,
 * and temporary differences. Effective-tax-rate sanity check.
 */
import { Finding, ValidationResult, DISCLAIMER } from './types';
import {
  CellValue, parseNumber, parsePercent, makeHeaderMap, normaliseHeader,
  acceptedHeadersFor, findingBuilder, severityCounts, compliantCount, fmt,
} from './_shared';

const VERSION = 'aasb112-1.0.0';
const fb = findingBuilder('AASB_112' as any, VERSION);

const HMAP = makeHeaderMap([
  ['period', 'period', 'fy', 'reporting period'],
  ['accounting_profit', 'accounting profit', 'pbt', 'profit before tax'],
  ['tax_rate', 'tax rate', 'company tax rate', 'statutory rate'],
  ['permanent_differences', 'permanent differences', 'permanent diff'],
  ['temporary_differences', 'temporary differences', 'temporary diff', 'movement'],
  ['current_tax_expense', 'current tax', 'current tax expense'],
  ['deferred_tax_expense', 'deferred tax', 'deferred tax expense'],
  ['total_tax_expense', 'total tax expense', 'tax expense'],
  ['npat', 'npat', 'net profit after tax'],
]);

const REQUIRED = ['accounting_profit', 'total_tax_expense'];

export function detectAASB112(headers: string[]) {
  const hits = new Set<string>();
  for (const r of headers) { const c = normaliseHeader(HMAP, String(r ?? '')); if (c) hits.add(c); }
  const matched = REQUIRED.filter(r => hits.has(r));
  const boost = ['tax', 'pbt', 'temporary', 'permanent'].some(k => headers.some(h => String(h ?? '').toLowerCase().includes(k)));
  return { confidence: Math.min(1, (matched.length / REQUIRED.length) * (boost ? 1.5 : 1)), matched, missing: REQUIRED.filter(r => !hits.has(r)) };
}

export function validateAASB112(headers: string[], data: CellValue[][], _asAt: Date = new Date()): ValidationResult {
  const findings: Finding[] = [];
  const schemaErrors: string[] = [];
  const idx: Record<string, number> = {};
  headers.forEach((h, i) => { const c = normaliseHeader(HMAP, String(h ?? '')); if (c) idx[c] = i; });
  for (const f of REQUIRED) if (idx[f] === undefined) schemaErrors.push(`Missing column "${f}". Accepted: ${acceptedHeadersFor(HMAP, f).join(', ')}.`);
  if (schemaErrors.length) {
    return { summary: { standard: 'AASB_112' as any, rulepackVersion: VERSION, rowsTested: 0, compliant: 0, byseverity: { info: 0, warning: 0, error: 0 }, runAt: new Date().toISOString() }, findings, schemaErrors, disclaimer: DISCLAIMER };
  }

  const rowIds: string[] = [];
  data.forEach((raw, i) => {
    const rowNum = i + 2;
    const get = (k: string) => raw[idx[k]];
    const id = String(idx.period !== undefined ? (get('period') ?? `row-${rowNum}`) : `row-${rowNum}`);
    rowIds.push(id);

    const missing = REQUIRED.filter(f => { const v = get(f); return v == null || v === ''; });
    if (missing.length) { findings.push(fb.build('AASB112.R01', 'error', 'Required field missing', `Row ${rowNum} (${id}): missing ${missing.join(', ')}.`, { rowId: id })); return; }

    const pbt = parseNumber(get('accounting_profit'))!;
    const totalTax = parseNumber(get('total_tax_expense'))!;
    const rate = idx.tax_rate !== undefined ? parsePercent(get('tax_rate')) : null;
    const permDiffs = idx.permanent_differences !== undefined ? (parseNumber(get('permanent_differences')) ?? 0) : 0;
    const tempDiffs = idx.temporary_differences !== undefined ? (parseNumber(get('temporary_differences')) ?? 0) : 0;
    const currentTax = idx.current_tax_expense !== undefined ? parseNumber(get('current_tax_expense')) : null;
    const deferredTax = idx.deferred_tax_expense !== undefined ? parseNumber(get('deferred_tax_expense')) : null;
    const npat = idx.npat !== undefined ? parseNumber(get('npat')) : null;

    // R02 tax rate sanity (AU: 25% BRE / 30% non-BRE; allow 0–35% range as sanity)
    if (rate !== null && (rate < 0.10 || rate > 0.35)) {
      findings.push(fb.build('AASB112.R02', 'warning', 'Tax rate outside expected AU range',
        `Period ${id}: rate ${(rate * 100).toFixed(2)}%. Australian company tax is 25% for base-rate entities (≤$50m turnover, ≤80% passive income) or 30% otherwise.`,
        { rowId: id, evidence: { rate } }));
    }

    // R03 current tax recalc
    if (rate !== null && currentTax !== null) {
      const expectedTaxableIncome = pbt + permDiffs - tempDiffs;
      const expectedCurrentTax = expectedTaxableIncome * rate;
      const tol = Math.max(100, 0.02 * Math.abs(expectedCurrentTax || 1));
      if (Math.abs(currentTax - expectedCurrentTax) > tol) {
        findings.push(fb.build('AASB112.R03', 'warning', 'Current tax expense does not reconcile to taxable-income calc',
          `Period ${id}: reported current tax $${fmt(currentTax)}, expected $${fmt(expectedCurrentTax)} ((PBT $${fmt(pbt)} + perm diffs $${fmt(permDiffs)} − temp diffs $${fmt(tempDiffs)}) × ${(rate * 100).toFixed(1)}%).`,
          { rowId: id, evidence: { reported: currentTax, expected: expectedCurrentTax, taxableIncome: expectedTaxableIncome } }));
      }
    }

    // R04 effective tax rate sanity
    if (pbt !== 0 && rate !== null) {
      const etr = totalTax / pbt;
      const drift = Math.abs(etr - rate);
      if (drift > 0.05) {
        findings.push(fb.build('AASB112.R04', 'info', 'Effective tax rate drifts from statutory',
          `Period ${id}: ETR ${(etr * 100).toFixed(2)}% vs statutory ${(rate * 100).toFixed(2)}% (drift ${(drift * 100).toFixed(2)}pp). Common drivers: permanent differences, R&D incentive, foreign-tax adjustments, prior-year true-ups, change in unrecognised deferred tax assets. AASB 112.81(c) requires reconciliation in notes.`,
          { rowId: id, evidence: { etr, statutory: rate, drift } }));
      }
    }

    // R05 current + deferred = total
    if (currentTax !== null && deferredTax !== null) {
      const sum = currentTax + deferredTax;
      const tol = Math.max(50, 0.005 * Math.abs(totalTax || 1));
      if (Math.abs(sum - totalTax) > tol) {
        findings.push(fb.build('AASB112.R05', 'error', 'Current + Deferred ≠ Total tax expense',
          `Period ${id}: current $${fmt(currentTax)} + deferred $${fmt(deferredTax)} = $${fmt(sum)}, but total reported $${fmt(totalTax)}.`,
          { rowId: id }));
      }
    }

    // R06 NPAT = PBT − Total tax
    if (npat !== null) {
      const expectedNpat = pbt - totalTax;
      const tol = Math.max(50, 0.005 * Math.abs(pbt || 1));
      if (Math.abs(npat - expectedNpat) > tol) {
        findings.push(fb.build('AASB112.R06', 'error', 'NPAT does not reconcile (PBT − tax)',
          `Period ${id}: reported NPAT $${fmt(npat)}, expected $${fmt(expectedNpat)} (PBT $${fmt(pbt)} − tax $${fmt(totalTax)}).`,
          { rowId: id }));
      }
    }
  });

  return {
    summary: { standard: 'AASB_112' as any, rulepackVersion: VERSION, rowsTested: rowIds.length, compliant: compliantCount(rowIds, findings), byseverity: severityCounts(findings), runAt: new Date().toISOString() },
    findings, disclaimer: DISCLAIMER,
  };
}
